import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import API from '../utils/api';
import { 
  FaBell, 
  FaBriefcase, 
  FaCheckCircle, 
  FaExclamationCircle, 
  FaInfoCircle,
  FaTrash,
  FaEnvelope,
  FaCalendar,
  FaClock,
  FaEye,
  FaBuilding,
  FaCheck,
  FaArrowRight
} from 'react-icons/fa';
import FAQSection from '../components/FAQSection';
import './Notifications.css';

const Notifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [filter, setFilter] = useState('all'); // all, unread, job_posted, application_status
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    fetchNotifications();
  }, [filter]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      if (filter !== 'all') {
        if (filter === 'unread') {
          params.append('read', 'false');
        } else {
          params.append('type', filter);
        }
      }
      
      const { data } = await API.get(`/notifications?${params.toString()}`);
      setNotifications(data.notifications || []);
      setUnreadCount(data.unreadCount || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await API.put(`/notifications/${notificationId}/read`);
      setNotifications(prev => prev.map(notif => 
        notif._id === notificationId ? { ...notif, read: true } : notif
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
      toast.error('Failed to mark notification as read');
    }
  };

  const markAllAsRead = async () => {
    try {
      await API.put('/notifications/mark-all-read');
      setNotifications(prev => prev.map(notif => ({ ...notif, read: true })));
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch (error) {
      console.error('Error marking all as read:', error);
      toast.error('Failed to mark all notifications as read');
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await API.delete(`/notifications/${notificationId}`);
      setNotifications(prev => prev.filter(notif => notif._id !== notificationId));
      toast.success('Notification deleted');
    } catch (error) {
      console.error('Error deleting notification:', error);
      toast.error('Failed to delete notification');
    }
  };



  const getNotificationLink = (notification) => {
    switch (notification.type) {
      case 'job_posted':
        return notification.data?.job ? `/jobs/${notification.data.job._id || notification.data.job}` : '/jobs';
      case 'application_status':
        return '/applications';
      case 'deadline_reminder':
        return notification.data?.job ? `/jobs/${notification.data.job._id || notification.data.job}` : '/jobs';
      case 'job_application_received':
        return '/my-jobs';
      case 'application_accepted':
      case 'application_status_change':
        return '/applications';
      default:
        return '#';
    }
  };

  const formatTime = (date) => {
    const now = new Date();
    const diff = now - new Date(date);
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 60) return `${minutes} minutes ago`;
    if (hours < 24) return `${hours} hours ago`;
    if (days < 7) return `${days} days ago`;
    return new Date(date).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="notif-page-container">
      <div className="notif-layout">
        
        {/* ── LEFT SIDEBAR (TEAL) ── */}
        <aside className="notif-sidebar">
          <div className="notif-icon-wrapper">
            <FaBell />
          </div>
          <h2>Notification Center</h2>
          <p>Stay updated on your job applications, new job matches, and messages from employers.</p>
          
          <div className="notif-stats-card">
            <div className="notif-stats-number">{unreadCount}</div>
            <div className="notif-stats-text">Unread<br/>Alerts</div>
          </div>
        </aside>

        {/* ── MAIN CONTENT ── */}
        <main className="notif-main">
          
          {/* Header & Filters */}
          <div className="notif-header-actions">
            <div className="notif-tabs">
              {[
                { key: 'all', label: 'All' },
                { key: 'unread', label: 'Unread' },
                { key: 'job_posted', label: 'New Jobs' },
                { key: 'application_status', label: 'Applications' },
                { key: 'deadline_reminder', label: 'Deadlines' }
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setFilter(tab.key)}
                  className={`notif-tab ${filter === tab.key ? 'active' : ''}`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {unreadCount > 0 && (
              <button onClick={markAllAsRead} className="notif-mark-all">
                <FaCheck /> Mark All Read
              </button>
            )}
          </div>

          {/* Notifications List */}
          <div className="notif-list">
            {notifications.length === 0 ? (
              <div className="notif-empty">
                <div className="notif-empty-icon">
                  <FaBell />
                </div>
                <h3>All Caught Up!</h3>
                <p>{filter === 'unread' ? 'You have no unread notifications.' : 'You have no notifications at the moment.'}</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification._id}
                  className={`notif-card ${!notification.read ? 'unread' : ''}`}
                >
                  <div className={`notif-icon ${notification.type || 'default'}`}>
                    {notification.type === 'job_posted' ? <FaBriefcase /> :
                     notification.type === 'application_status' ? <FaCheckCircle /> :
                     notification.type === 'deadline_reminder' ? <FaClock /> :
                     <FaBell />}
                  </div>

                  <div className="notif-content">
                    <h3 className="notif-title">{notification.title}</h3>
                    <p className="notif-message">{notification.message}</p>
                    
                    <div className="notif-meta">
                      <span className="notif-meta-item">
                        <FaClock /> {formatTime(notification.createdAt)}
                      </span>
                      {notification.data?.company && (
                        <span className="notif-meta-item">
                          <FaBuilding /> {notification.data.company.name || 'Company'}
                        </span>
                      )}
                    </div>

                    {getNotificationLink(notification) !== '#' && (
                      <Link
                        to={getNotificationLink(notification)}
                        onClick={() => !notification.read && markAsRead(notification._id)}
                        className="notif-link"
                      >
                        View Details <FaArrowRight />
                      </Link>
                    )}
                  </div>

                  <div className="notif-actions">
                    {!notification.read && (
                      <button
                        onClick={() => markAsRead(notification._id)}
                        className="notif-action-btn notif-btn-read"
                        title="Mark as read"
                      >
                        <FaCheck />
                      </button>
                    )}
                    <button
                      onClick={() => deleteNotification(notification._id)}
                      className="notif-action-btn notif-btn-delete"
                      title="Delete"
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>
              ))
            )}
            
            {/* Load More (Pagination Logic placeholder) */}
            {notifications.length > 0 && notifications.length % 20 === 0 && (
              <div className="mt-8 text-center">
                <button className="notif-mark-all mx-auto bg-white border border-gray-200 text-gray-700 hover:bg-gray-50">
                  Load More
                </button>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* ── FAQ SECTION ── */}
      <FAQSection 
        title="Notifications FAQ"
        subtitle="Common questions about your alerts."
        faqs={[
          { q: 'How often do I receive notifications?', a: 'You will receive notifications immediately for important events like application updates or messages, and daily for new job matches.' },
          { q: 'Can I turn off email notifications?', a: 'Yes, you can manage your email preferences from your account settings.' },
          { q: 'Why am I not getting any alerts?', a: 'Ensure you have set up your job alerts properly with the correct keywords and locations.' },
          { q: 'Do notifications expire?', a: 'Notifications are kept for 30 days before being automatically cleared from your dashboard.' },
          { q: 'How do I mark all as read?', a: 'Click the "Mark all as read" button at the top right of your notifications list.' }
        ]}
      />
    </div>
  );
};

export default Notifications;